import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { apiService, CustomFieldDefinition } from '../services/api';
import './CustomFieldModal.css';

interface CustomFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFieldCreated: (field: CustomFieldDefinition) => void;
}

const CustomFieldModal: React.FC<CustomFieldModalProps> = ({ isOpen, onClose, onFieldCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'text' as 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'email' | 'phone' | 'person',
    options: [] as string[],
    required: false
  });
  // Track if user manually edited the name; if true, we stop auto-syncing with label
  const [isNameEdited, setIsNameEdited] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate a backend-safe field name from a label
  const generateFieldName = (label: string): string => {
    const lower = (label || '').trim().toLowerCase();
    // Replace any non-alphanumeric with underscores, collapse multiples, trim
    let name = lower.replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    // Ensure starts with a letter
    if (!/^[a-z]/.test(name) && name.length > 0) {
      name = `f_${name}`;
    }
    return name;
  };

  const handleInputChange = (field: string, value: any) => {
    setError(null);
    // Special handling: auto-fill name from label until user edits name
    if (field === 'label') {
      const nextLabel = value as string;
      setFormData(prev => ({
        ...prev,
        label: nextLabel,
        name: isNameEdited ? prev.name : generateFieldName(nextLabel)
      }));
      return;
    }

    if (field === 'name') {
      setIsNameEdited(true);
      const raw = String(value);
      // Sanitize in-place: allow letters, numbers, underscores; convert dashes/spaces to underscore
      const sanitized = raw
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
      setFormData(prev => ({ ...prev, name: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddOption = () => {
    if (newOption.trim() && !formData.options.includes(newOption.trim())) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form
      if (!formData.name.trim()) {
        // If name empty, attempt to generate from label
        const generated = generateFieldName(formData.label);
        if (!generated) {
          throw new Error('Field name is required');
        }
        formData.name = generated;
      }
      if (!formData.label.trim()) {
        throw new Error('Field label is required');
      }
      if (['select', 'multiselect'].includes(formData.type) && formData.options.length === 0) {
        throw new Error('Options are required for select and multiselect fields');
      }

      // Final sanitize name and ensure it matches backend regex
      let fieldName = formData.name.trim().toLowerCase();
      fieldName = fieldName
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
      if (!/^[a-z][a-z0-9_]*$/.test(fieldName)) {
        // Prefix if first char invalid after sanitation
        if (fieldName.length === 0) {
          fieldName = generateFieldName(formData.label);
        } else {
          fieldName = `f_${fieldName}`;
        }
      }
      if (!/^[a-z][a-z0-9_]*$/.test(fieldName)) {
        throw new Error('Field name must start with a letter and contain only letters, numbers, and underscores');
      }

      const fieldData = {
        name: fieldName,
        label: formData.label.trim(),
        type: formData.type,
        options: formData.options,
        required: formData.required
      };

      const newField = await apiService.createCustomField(fieldData);
      onFieldCreated(newField);
      
      // Reset form
      setFormData({
        name: '',
        label: '',
        type: 'text',
        options: [],
        required: false
      });
      setNewOption('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create custom field');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Custom Column</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="label">Column Label *</label>
            <input
              id="label"
              type="text"
              value={formData.label}
              onChange={(e) => handleInputChange('label', e.target.value)}
              placeholder="e.g., Budget Range, Decision Timeline"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Field Name</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Auto-generated from label if empty"
            />
            <small>Used internally (letters, numbers, underscores only). Auto-filled from label.</small>
          </div>

          <div className="form-group">
            <label htmlFor="type">Field Type *</label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              required
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="select">Single Select</option>
              <option value="multiselect">Multi Select</option>
              <option value="boolean">Yes/No</option>
              <option value="url">URL (clickable link)</option>
              <option value="email">Email (clickable mailto)</option>
              <option value="phone">Phone (clickable tel)</option>
              <option value="person">Person</option>
            </select>
          </div>

          {['select', 'multiselect'].includes(formData.type) && (
            <div className="form-group">
              <label>Options *</label>
              <div className="options-container">
                {formData.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <span>{option}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="remove-option"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="add-option">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Add option..."
                  />
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="add-option-button"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => handleInputChange('required', e.target.checked)}
              />
              <span>Required field</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Creating...' : 'Create Column'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomFieldModal;
